/**
 * A-Z Heat Pump Course - Table of Contents
 * Comprehensive encyclopedia-style course material overview
 * @module AZCourseIntro
 */

import React from 'react';
import { Typography } from 'antd';
import { 
  ExperimentOutlined,
  ApartmentOutlined,
  FileSearchOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import styles from './Intro.module.css';
import PagesWrapper from '../../../PagesWrapper';
import { courseStructure } from './courseStructure';

const { Title, Paragraph, Text } = Typography;

/**
 * A-Z Course Introduction Component
 * Renders the complete table of contents in encyclopedia style
 */
const AZCourseIntro = () => {
  const navigate = useNavigate();

  const handleNavigate = (link) => {
    if (link) {
      navigate(link);
    }
  };

  /**
   * Get icon component based on section ID
   */
  const getIcon = (sectionId) => {
    const iconMap = {
      'cold-room': <ExperimentOutlined />,
      'heat-input': <ApartmentOutlined />,
      'two-room-scenarios': <ApartmentOutlined />,
      'case-studies': <FileSearchOutlined />,
    };
    return iconMap[sectionId] || <ExperimentOutlined />;
  };

  return (
    <PagesWrapper>
      <div className={styles.introContainer}>
        {/* Page Header */}
        <header className={styles.header}>
          <Title level={1}>A-Z Heat Pump Course</Title>
          <Paragraph className={styles.subtitle}>
            Comprehensive Technical Reference for Heat Pump Systems
          </Paragraph>
        </header>

        {/* Introduction Block */}
        <section className={styles.introBlock}>
          <div className={styles.introContent}>
            <Text className={styles.introText}>
              This course provides an in-depth exploration of heat pump technology, covering fundamental 
              thermodynamic principles through advanced system optimization techniques. Each section is 
              designed to build upon previous knowledge, creating a comprehensive understanding of 
              modern heating systems.
            </Text>
          </div>
          <div className={styles.statsBar}>
            <div className={styles.stat}>
              <span className={styles.statNumber}>4</span>
              <span className={styles.statLabel}>Main Sections</span>
            </div>
            <div className={styles.stat}>
              <span className={styles.statNumber}>21</span>
              <span className={styles.statLabel}>Topics</span>
            </div>
          </div>
        </section>

        {/* Table of Contents */}
        <nav className={styles.tableOfContents}>
          <div className={styles.tocHeader}>
            <Title level={3}>Contents</Title>
          </div>

          {courseStructure.map((section, sectionIndex) => (
            <article key={section.id} className={styles.section}>
              {/* Section Header */}
              <div className={styles.sectionHeader}>
                <div className={styles.sectionIcon}>{getIcon(section.id)}</div>
                <div className={styles.sectionMeta}>
                  <Title level={2} className={styles.sectionTitle}>
                    <span className={styles.sectionNumber}>{section.number}.</span>
                    {section.title}
                  </Title>
                  <Paragraph className={styles.sectionDescription}>
                    {section.description}
                  </Paragraph>
                </div>
              </div>

              {/* Subsections List */}
              <div className={styles.subsectionsList}>
                {section.subsections.map((sub, subIndex) => (
                  <div 
                    key={sub.id} 
                    className={styles.subsectionItem}
                    onClick={() => handleNavigate(sub.link)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => e.key === 'Enter' && handleNavigate(sub.link)}
                  >
                    <div className={styles.subsectionNumber}>{sub.id}</div>
                    <div className={styles.subsectionContent}>
                      <Text className={styles.subsectionTitle}>{sub.title}</Text>
                      <Text className={styles.subsectionDescription}>{sub.description}</Text>
                    </div>
                    <div className={styles.subsectionArrow}>→</div>
                  </div>
                ))}
              </div>
            </article>
          ))}
        </nav>

        {/* Footer Note */}
        <footer className={styles.footer}>
          <Text className={styles.footerText}>
            © HeatCraft HP Learning Platform • Content is continuously updated to reflect the latest industry standards.
          </Text>
        </footer>
      </div>
    </PagesWrapper>
  );
};

export default AZCourseIntro;
